import { memo, useState } from "react";
import { type CadTabProps } from "~/features/productDetails/components/PdpTabs/CadTab/CadTab";

import classes from "~/features/productDetails/components/PdpTabs/CadTab/EmptyCadTab.module.less";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { Button, Stack } from "~/common/mui";
import { Icon } from "~/common/components/icon/Icon";
import LoginPopup from "~/features/auth/login/components/LoginPopup";
import ResetPasswordPopup from "~/features/auth/login/components/ResetPasswordPopup";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { isLoginPopupInitiallyOpen } from "~/features/auth/login/loginUtils";
import { IconType } from "~/common/components/icon/IconType";
import { IconSize } from "~/common/components/icon/IconSize";
import { Trans, useTranslation } from "react-i18next";
import { requestCadNotification } from "~/features/productDetails/components/PdpTabs/CadTab/api/requestCadNotification";
import { trackPdpRequestCADNotify, trackPdpRequestCADNotifyResult } from "~/app/analytics/ga/ga";

const EmptyCadTab = (props: CadTabProps) => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(props.product.customerRequestedCADAvailability);
  const [submitting, setSubmitting] = useState(false);

  const isLoggedIn = userIsLoggedIn();

  const {
    val: loginPopupOpen,
    setTrue: openLogin,
    setFalse: closeLogin,
  } = useBooleanState(() => isLoginPopupInitiallyOpen());
  const { val: resetPopupOpen, setTrue: openReset, setFalse: closeReset } = useBooleanState(false);

  const [email, setEmail] = useState("");

  const onResetClick = (email: string) => {
    setEmail(email);
    closeLogin();
    openReset();
  };

  const subscribe = async () => {
    trackPdpRequestCADNotify();
    setSubmitting(true);
    try {
      await requestCadNotification(props.product.code);
      trackPdpRequestCADNotifyResult(true);
    } catch {
      trackPdpRequestCADNotifyResult(false);
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  const cta = (
    <Button
      startIcon={<Icon type={IconType.Envelope} />}
      onClick={isLoggedIn ? subscribe : openLogin}
      loading={submitting}
    >
      {t(`page.pdp.cad.sorry.cta.${isLoggedIn ? "notify" : "login"}`)}
    </Button>
  );

  let content;
  if (!submitted) {
    content = (
      <Stack gap={2} direction="column" className={classes.contentContainer}>
        <h2 className={classes.title}>{t("page.pdp.cad.sorry.title")}</h2>
        <p className={classes.text}>{t(`page.pdp.cad.sorry.content.${isLoggedIn ? "user" : "anonymous"}`)}</p>
        {cta}
      </Stack>
    );
  } else {
    const designation = props.product.designation;
    content = (
      <Stack gap={4} direction="column" className={classes.contentContainer}>
        <Icon size={IconSize.FONT_64} type={IconType.CheckCircle} className={"f-green"} />
        <Trans i18nKey={"page.pdp.cad.sorry.success"}>
          Thank you for your interest. We will notify you as soon as the CAD file for <b>{{ designation }}</b>{" "}
          is available.
        </Trans>
      </Stack>
    );
  }

  return (
    <>
      <div className={classes.root}>{content}</div>
      {loginPopupOpen && (
        <LoginPopup
          initialEmail={""}
          onResetClick={onResetClick}
          close={closeLogin}
          alternativeGAAction={"Sign In CAD Tab"}
        />
      )}
      {resetPopupOpen && <ResetPasswordPopup initialEmail={email} close={closeReset} />}
    </>
  );
};

export default memo(EmptyCadTab);
